// src/utils/insurance-stats.utils.ts

import type {
  Authorization,
  Claim,
  Enrollment,
  Policy,
  Provider,
} from "../contexts/InsuranceContextType";

export interface InsuranceStats {
  totalClaims: number;
  pendingClaims: number;
  approvedClaims: number;
  rejectedClaims: number;
  processingClaims: number;

  activePolicies: number;
  expiredPolicies: number;

  activeEnrollments: number;
  pendingAuthorizations: number;
  activeProviders: number;

  totalClaimsValue: number;
  averageClaimAmount: number;
}

interface GenerateInsuranceStatsParams {
  claims: Claim[];
  policies: Policy[];
  enrollments: Enrollment[];
  authorizations: Authorization[];
  providers: Provider[];
}

export function generateInsuranceStats({
  claims,
  policies,
  enrollments,
  authorizations,
  providers,
}: GenerateInsuranceStatsParams): InsuranceStats {
  const totalClaims = claims.length;

  const pendingClaims = claims.filter(
    (claim) => claim.status === "pending"
  ).length;

  const approvedClaims = claims.filter(
    (claim) => claim.status === "approved"
  ).length;

  const rejectedClaims = claims.filter(
    (claim) => claim.status === "rejected"
  ).length;

  const processingClaims = claims.filter(
    (claim) => claim.status === "processing"
  ).length;

  const activePolicies = policies.filter(
    (policy) => policy.status === "active"
  ).length;

  const expiredPolicies = policies.filter(
    (policy) => policy.status === "expired"
  ).length;

  const activeEnrollments = enrollments.filter(
    (enrollment) => enrollment.status === "active"
  ).length;

  const pendingAuthorizations =
    authorizations.filter(
      (authorization) =>
        authorization.status === "pending"
    ).length;

  const activeProviders = providers.filter(
    (provider) => provider.status === "active"
  ).length;

  const totalClaimsValue = claims.reduce(
    (total, claim) =>
      total + claim.claimAmount,
    0
  );

  const averageClaimAmount =
    totalClaims > 0
      ? totalClaimsValue / totalClaims
      : 0;

  return {
    totalClaims,
    pendingClaims,
    approvedClaims,
    rejectedClaims,
    processingClaims,

    activePolicies,
    expiredPolicies,

    activeEnrollments,
    pendingAuthorizations,
    activeProviders,

    totalClaimsValue,
    averageClaimAmount,
  };
}